// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import AdminLogin from './screens/AdminLogin';
import PrivateRoute from './components/PrivateRoute';
import Bookings from './screens/Bookings';
import StopZones from './screens/StopZones';
import RestAreas from './screens/RestAreas';
import Dashboard from './screens/Dashboard';
import Sidebar from './components/Sidebar';
import { Box, Toolbar, CircularProgress, Container, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { generateTheme } from './theme';

const App = () => {
  const [theme, setTheme] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem('adminToken')
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    setIsAuthenticated(!!token);
    
    const fetchTheme = async () => {
      try {
        const theme = await generateTheme();
        setTheme(theme);
      } catch (error) {
        console.error('Error generating theme:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTheme();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
  };

  // Loading state while theme is being fetched
  if (loading) {
    return (
      <Container 
        sx={{ 
          height: '100vh', 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center' 
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Caricamento applicazione...
        </Typography>
      </Container>
    );
  }

  if (!theme) return null;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
          {isAuthenticated && <Sidebar onLogout={handleLogout} />}
          
          <Box 
            component="main" 
            sx={{ 
              flexGrow: 1, 
              p: 3,
              marginLeft: 0,
              transition: 'margin 0.3s ease',
              width: isAuthenticated ? 'calc(100% - 72px)' : '100%',
            }}
          >
            {isAuthenticated && <Toolbar />}
            
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              
              <Route
                path="/login"
                element={
                  isAuthenticated ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <AdminLogin setIsAuthenticated={setIsAuthenticated} />
                  )
                }
              />
              
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              
              <Route
                path="/bookings"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <Bookings />
                  </PrivateRoute>
                }
              />
              
              <Route
                path="/stopzones"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <StopZones />
                  </PrivateRoute>
                }
              />
              
              <Route
                path="/stopzones/:id"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <RestAreas />
                  </PrivateRoute>
                }
              />
              
              {/* Redirect to login if no routes match */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
};

export default App;